import { create } from "zustand";
import type { LlmSettings } from "../services/llm";
import {
  COMBO_KEY_PREFIX,
  FEATURE_IDS,
  type EndpointCombo,
  type FeatureId,
  type FeatureRouteMap,
} from "../services/featureRoutes";
import {
  DEFAULT_MAIN_PRESET,
  parseMainPromptPreset,
  type MainPromptPreset,
} from "../services/presetAssembler";

const SETTINGS_KEY = "mh-rp-llm-settings";
const ROUTING_KEY = "mh-rp-endpoint-routing-v1";
const saved = (() => {
  try { return JSON.parse(localStorage.getItem(SETTINGS_KEY) ?? "{}"); }
  catch { return {}; }
})();
const legacyKey = sessionStorage.getItem("mh-rp-api-key") ?? "";
const legacyEmbeddingKey = sessionStorage.getItem("mh-rp-embedding-key") ?? "";
const initialMainPreset = (() => {
  try {
    return parseMainPromptPreset(saved.mainPreset ?? DEFAULT_MAIN_PRESET);
  } catch {
    return structuredClone(DEFAULT_MAIN_PRESET);
  }
})();

const generationFeatures = FEATURE_IDS.filter(
  (feature) => feature !== "rag-embedding",
);
const evolutionStageFeatures = [
  "evolution-entry",
  "evolution-item",
  "evolution-player",
  "evolution-npc",
  "evolution-monster",
  "evolution-misc",
] as const satisfies readonly FeatureId[];

function hasDistinctEvolutionRoutes(featureRoutes: FeatureRouteMap) {
  const configured = evolutionStageFeatures.map((feature) => featureRoutes[feature] ?? []);
  return configured.some((route) => route.length > 0) && new Set(configured.map((route) => JSON.stringify(route))).size > 1;
}

function initialRouting(): {
  endpointCombos: EndpointCombo[];
  featureRoutes: FeatureRouteMap;
} {
  const stored = localStorage.getItem(ROUTING_KEY);
  if (stored) {
    try {
      const parsed = JSON.parse(stored) as {
        endpointCombos?: EndpointCombo[];
        featureRoutes?: FeatureRouteMap;
      };
      if (Array.isArray(parsed.endpointCombos))
        {
          const featureRoutes = { ...(parsed.featureRoutes ?? {}) };
          if (featureRoutes.evolution === undefined) {
            const inherited = evolutionStageFeatures.map((feature) => featureRoutes[feature]).find((ids) => ids?.length);
            if (inherited) featureRoutes.evolution = [...inherited];
          }
          const generationId = parsed.endpointCombos.find(
            (combo) => combo.enabled && combo.kind === "generation",
          )?.id;
          const embeddingId = parsed.endpointCombos.find(
            (combo) => combo.enabled && combo.kind === "embedding",
          )?.id;
          for (const feature of FEATURE_IDS) {
            if (featureRoutes[feature] !== undefined) continue;
            const fallback = feature === "rag-embedding" ? embeddingId : generationId;
            if (fallback) featureRoutes[feature] = [fallback];
          }
          localStorage.setItem(
            ROUTING_KEY,
            JSON.stringify({ endpointCombos: parsed.endpointCombos, featureRoutes }),
          );
          return { endpointCombos: parsed.endpointCombos, featureRoutes };
        }
    } catch {
      // Invalid routing metadata falls back to the idempotent legacy migration.
    }
  }

  const generation: EndpointCombo = {
    id: "legacy-default",
    name: "默认生成端点",
    kind: "generation",
    enabled: true,
    baseUrl: saved.baseUrl ?? "https://api.openai.com/v1",
    model: saved.model ?? "gpt-5-mini",
    useProxy: saved.useProxy ?? true,
    temperature: saved.temperature ?? 0.8,
  };
  const endpointCombos = [generation];
  const featureRoutes = Object.fromEntries(
    generationFeatures.map((feature) => [feature, [generation.id]]),
  ) as FeatureRouteMap;
  if (
    legacyKey &&
    !sessionStorage.getItem(`${COMBO_KEY_PREFIX}${generation.id}`)
  )
    sessionStorage.setItem(`${COMBO_KEY_PREFIX}${generation.id}`, legacyKey);

  if (saved.embeddingUrl) {
    const embedding: EndpointCombo = {
      id: "legacy-embedding",
      name: "默认 Embedding 端点",
      kind: "embedding",
      enabled: true,
      baseUrl: saved.embeddingUrl,
      model: saved.embeddingModel ?? "Qwen/Qwen3-Embedding-8B",
      useProxy: saved.embeddingUseProxy ?? true,
      dimensions: 1024,
    };
    endpointCombos.push(embedding);
    featureRoutes["rag-embedding"] = [embedding.id];
    if (
      legacyEmbeddingKey &&
      !sessionStorage.getItem(`${COMBO_KEY_PREFIX}${embedding.id}`)
    )
      sessionStorage.setItem(
        `${COMBO_KEY_PREFIX}${embedding.id}`,
        legacyEmbeddingKey,
      );
  }
  localStorage.setItem(
    ROUTING_KEY,
    JSON.stringify({ endpointCombos, featureRoutes }),
  );
  return { endpointCombos, featureRoutes };
}

const routing = initialRouting();

export interface MediaSettings {
  provider:
    | "openai"
    | "gemini"
    | "grok"
    | "wavespeed"
    | "qwen"
    | "doubao"
    | "custom_openai"
    | "comfyui"
    | "novelai";
  baseUrl: string;
  model: string;
  portraitAuto: boolean;
  storyEnabled: boolean;
  storyMode: "independent_parallel" | "sequential";
  storyImageCount: number;
  stylePrompt: string;
}

interface State extends LlmSettings {
  endpointCombos: EndpointCombo[];
  featureRoutes: FeatureRouteMap;
  mainPreset: MainPromptPreset;
  mainPresetProfileId: string;
  media: MediaSettings;
  cloudAutoSync: boolean;
  evolutionRoutesSplit: boolean;
  set: (patch: Partial<Omit<State, "set">>) => void;
  upsertCombo: (combo: EndpointCombo) => void;
  moveCombo: (id: string, offset: number) => void;
  removeCombo: (id: string) => void;
  setComboKey: (id: string, key: string) => void;
  setFeatureRoute: (feature: FeatureId, comboIds: string[]) => void;
  setManyFeatureRoutes: (entries: readonly { feature: FeatureId; comboIds: string[] }[]) => void;
}

function persist(next: State) {
  localStorage.setItem(
    SETTINGS_KEY,
    JSON.stringify({
      baseUrl: next.baseUrl,
      model: next.model,
      useProxy: next.useProxy,
      temperature: next.temperature,
      evolutionEnabled: next.evolutionEnabled,
      evolutionRoutesSplit: next.evolutionRoutesSplit,
      media: next.media,
      cloudAutoSync: next.cloudAutoSync,
      embeddingUrl: next.embedding?.baseUrl,
      embeddingModel: next.embedding?.model,
      embeddingUseProxy: next.embedding?.useProxy,
      mainPreset: next.mainPreset,
      mainPresetProfileId: next.mainPresetProfileId,
    }),
  );
  localStorage.setItem(
    ROUTING_KEY,
    JSON.stringify({
      endpointCombos: next.endpointCombos,
      featureRoutes: next.featureRoutes,
    }),
  );
}

export const useSettingsStore = create<State>((set) => ({
  baseUrl: saved.baseUrl ?? "https://api.openai.com/v1",
  model: saved.model ?? "gpt-5-mini",
  apiKey: legacyKey,
  useProxy: saved.useProxy ?? true,
  temperature: saved.temperature ?? 0.8,
  evolutionEnabled: saved.evolutionEnabled ?? true,
  evolutionRoutesSplit: typeof saved.evolutionRoutesSplit === "boolean" ? saved.evolutionRoutesSplit : hasDistinctEvolutionRoutes(routing.featureRoutes),
  endpointCombos: routing.endpointCombos,
  featureRoutes: routing.featureRoutes,
  mainPreset: initialMainPreset,
  mainPresetProfileId: saved.mainPresetProfileId ?? "mh-default",
  media: {
    provider: saved.media?.provider ?? "openai",
    baseUrl: saved.media?.baseUrl ?? "https://api.openai.com/v1",
    model: saved.media?.model ?? "gpt-image-1",
    portraitAuto: saved.media?.portraitAuto ?? false,
    storyEnabled: saved.media?.storyEnabled ?? false,
    storyMode: saved.media?.storyMode ?? "independent_parallel",
    storyImageCount: saved.media?.storyImageCount ?? 1,
    stylePrompt:
      saved.media?.stylePrompt ??
      "旧大陆猎人手记，写实手绘概念艺术，粗粝羊皮纸纹理",
  },
  cloudAutoSync: saved.cloudAutoSync ?? false,
  embedding: saved.embeddingUrl
    ? {
        baseUrl: saved.embeddingUrl,
        model: saved.embeddingModel ?? "Qwen/Qwen3-Embedding-8B",
        apiKey: legacyEmbeddingKey,
        useProxy: saved.embeddingUseProxy ?? true,
        dimensions: 1024,
      }
    : undefined,
  set: (patch) =>
    set((state) => {
      let endpointCombos = patch.endpointCombos ?? state.endpointCombos;
      if (
        patch.baseUrl !== undefined ||
        patch.model !== undefined ||
        patch.useProxy !== undefined ||
        patch.temperature !== undefined
      )
        endpointCombos = endpointCombos.map((combo) =>
          combo.id === "legacy-default"
            ? {
                ...combo,
                baseUrl: patch.baseUrl ?? combo.baseUrl,
                model: patch.model ?? combo.model,
                useProxy: patch.useProxy ?? combo.useProxy,
                temperature: patch.temperature ?? combo.temperature,
              }
            : combo,
        );
      if (patch.embedding)
        endpointCombos = endpointCombos.map((combo) =>
          combo.id === "legacy-embedding"
            ? {
                ...combo,
                baseUrl: patch.embedding?.baseUrl ?? combo.baseUrl,
                model: patch.embedding?.model ?? combo.model,
                useProxy: patch.embedding?.useProxy ?? combo.useProxy,
                dimensions: patch.embedding?.dimensions ?? combo.dimensions,
              }
            : combo,
        );
      const next = { ...state, ...patch, endpointCombos };
      if (patch.apiKey !== undefined) {
        sessionStorage.setItem("mh-rp-api-key", patch.apiKey);
        if (endpointCombos.some((combo) => combo.id === "legacy-default"))
          sessionStorage.setItem(
            `${COMBO_KEY_PREFIX}legacy-default`,
            patch.apiKey,
          );
      }
      if (patch.embedding?.apiKey !== undefined) {
        sessionStorage.setItem("mh-rp-embedding-key", patch.embedding.apiKey);
        if (endpointCombos.some((combo) => combo.id === "legacy-embedding"))
          sessionStorage.setItem(
            `${COMBO_KEY_PREFIX}legacy-embedding`,
            patch.embedding.apiKey,
          );
      }
      persist(next);
      return next;
    }),
  upsertCombo: (combo) =>
    set((state) => {
      const endpointCombos = state.endpointCombos.some(
        (item) => item.id === combo.id,
      )
        ? state.endpointCombos.map((item) =>
            item.id === combo.id ? combo : item,
          )
        : [...state.endpointCombos, combo];
      const next = { ...state, endpointCombos };
      persist(next);
      return next;
    }),
  moveCombo: (id, offset) =>
    set((state) => {
      const endpointCombos = [...state.endpointCombos];
      const index = endpointCombos.findIndex((combo) => combo.id === id);
      const target = index + offset;
      if (index < 0 || target < 0 || target >= endpointCombos.length) return state;
      [endpointCombos[index], endpointCombos[target]] = [endpointCombos[target]!, endpointCombos[index]!];
      const next = { ...state, endpointCombos };
      persist(next);
      return next;
    }),
  removeCombo: (id) =>
    set((state) => {
      const endpointCombos = state.endpointCombos.filter(
        (combo) => combo.id !== id,
      );
      const featureRoutes = Object.fromEntries(
        Object.entries(state.featureRoutes).map(([feature, ids]) => [
          feature,
          ids?.filter((value) => value !== id) ?? [],
        ]),
      ) as FeatureRouteMap;
      sessionStorage.removeItem(`${COMBO_KEY_PREFIX}${id}`);
      const next = { ...state, endpointCombos, featureRoutes };
      persist(next);
      return next;
    }),
  setComboKey: (id, key) => {
    sessionStorage.setItem(`${COMBO_KEY_PREFIX}${id}`, key);
    set((state) => ({ ...state }));
  },
  setFeatureRoute: (feature, comboIds) =>
    set((state) => {
      const next = {
        ...state,
        featureRoutes: { ...state.featureRoutes, [feature]: comboIds },
      };
      persist(next);
      return next;
    }),
  setManyFeatureRoutes: (entries) =>
    set((state) => {
      if (!entries.length) return state;
      const featureRoutes = { ...state.featureRoutes };
      for (const { feature, comboIds } of entries)
        featureRoutes[feature] = [...new Set(comboIds)];
      const next = { ...state, featureRoutes };
      persist(next);
      return next;
    }),
}));
